import type { CSSProperties } from 'react';

/**
 * Temporary bridge for public shared-feature props that previously accepted
 * MUI's `sx`. It keeps callers source-compatible while the feature surfaces
 * move to semantic elements and design-system primitives.
 */
const isStyleObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

export const toInlineStyle = (style: unknown): CSSProperties | undefined => {
  const styles = Array.isArray(style) ? style : [style];
  const inlineStyle: Record<string, string | number> = {};

  styles.forEach(entry => {
    if (!isStyleObject(entry)) return;

    Object.entries(entry).forEach(([property, value]) => {
      if (property.startsWith('&') || typeof value === 'object' || typeof value === 'function') {
        return;
      }

      if (typeof value === 'string' || typeof value === 'number') {
        inlineStyle[property] = value;
      }
    });
  });

  return Object.keys(inlineStyle).length ? inlineStyle : undefined;
};

export const getChildInlineStyle = (style: unknown): CSSProperties | undefined => {
  const styles = Array.isArray(style) ? style : [style];
  const childStyle = styles.reduce<Record<string, unknown>>((accumulator, entry) => {
    if (!isStyleObject(entry)) return accumulator;
    const nestedStyle = entry['& > *'];

    return isStyleObject(nestedStyle) ? { ...accumulator, ...nestedStyle } : accumulator;
  }, {});

  return toInlineStyle(childStyle);
};
